/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useEffect } from "react";
import DefaultCell from "components/generals/DefaultCell";
import { CoreAPI_URLs } from "constants/http.const";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import useHttp from "hooks/http.hook";
import { TableProps } from "interfaces";
import { useTranslation } from "react-i18next";
import { InsuranceTransactionInterface } from "@malmur-technologies/medicalsystem-shared";
import { QueryResultInterface } from "@malmur-technologies/medicalsystem-shared";

/**
 * Insurance transaction table component for displaying insurance claim transactions.
 * 
 * Features:
 * - Displays insurance transaction records in sortable data table
 * - Auto-loads all transactions on mount
 * - Columns: date, insurance name, folio, status, claim amount, policy ID
 * - Formatted date display (es-MX locale: DD/MM/YYYY)
 * - Nested property access for insurance and policy data
 * - Safe array handling
 * 
 * Table Columns:
 * - Date: Transaction creation date (formatted)
 * - Insurance: Insurance provider name from patientInsurance.insurance.name
 * - Folio: Transaction folio/reference number
 * - Status: Transaction status (pending, approved, denied, etc.)
 * - Claim: Claim amount
 * - Policy: Policy ID from patientInsurance.policyId
 * 
 * Use Cases:
 * - View all insurance transactions
 * - Track claim status
 * - Review insurance payments
 * - Audit insurance transactions
 * 
 * @param {TableProps<InsuranceTransactionInterface>} props - Table properties
 * @param {InsuranceTransactionInterface[]} props.modelList - Array of transaction records
 * @param {Function} props.setModelList - Callback to update transaction list
 * 
 * @returns {JSX.Element} Insurance transaction table with formatted display
 */
const InsuranceTransactionTable: React.FC<TableProps<InsuranceTransactionInterface>> = ({
  modelList,
  setModelList,
  ...props
}) => {

  const { t } = useTranslation();
  const [_isLoading, _error, sendRequest] = useHttp();

  useEffect(() => {
      sendRequest<QueryResultInterface<InsuranceTransactionInterface>, void>(
        CoreAPI_URLs.INSURANCE_TRANSACTION_GET_ALL,
        (result) => {
          setModelList(result.results);
        },
        "GET"
      );
  }, []);

  const getUITable = (list: InsuranceTransactionInterface[]) => {
    const safeList = Array.isArray(list) ? list : [];
    return {
      columns: [
        {
        Header: t("billingEncounterTable.date"),
        accessor: (row: InsuranceTransactionInterface) => {
          const date = new Date(row.createdAt);
          return date.toLocaleDateString('es-MX', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit'
          });
        },
        Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("InsuranceTransactionsTable.insurance"),
          accessor: (row: InsuranceTransactionInterface) => row.patientInsurance.insurance.name,
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("InsuranceTransactionsTable.folio"),
          accessor: "folio",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("InsuranceTransactionsTable.status"),
          accessor: "status",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={t(`InsuranceTransactionsTable.${value}`)} />,
        },
        {
          Header: t("InsuranceTransactionsTable.claim"),
          accessor: "claim",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("InsuranceTransactionsTable.policy"),
          accessor: (row: InsuranceTransactionInterface) => row.patientInsurance.policyId,
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
      ],
      rows: safeList,
    };
  };

  return (<DataTable {...props} table={getUITable(modelList)} />);
};

export default InsuranceTransactionTable;